import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PLANET_IMAGES from './planetImages';
import styles from './styles.module.scss';

// Rising has no planet artwork: a small comet stands in for the horizon.
const RisingArt = () => (
    <svg className={styles.bigThreeImg} viewBox="0 0 44 44" aria-hidden="true" focusable="false">
        <defs>
            <linearGradient id="bc-rising-tail" x1="0" y1="1" x2="0.5" y2="0">
                <stop offset="0" stopColor="#3DDBA0" stopOpacity="0" />
                <stop offset="1" stopColor="#3DDBA0" stopOpacity="0.9" />
            </linearGradient>
        </defs>
        <path d="M10 44C14 30 18 18 22 6C26 18 28 30 30 44Z" fill="url(#bc-rising-tail)" />
        <circle cx="22" cy="6" r="3.5" fill="#B5FFE3" />
    </svg>
);

const LockedArt = () => (
    <span className={classNames(styles.bigThreeImg, styles.lockedArt)} aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6">
            <rect x="5" y="11" width="14" height="9" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" strokeLinecap="round" />
        </svg>
    </span>
);

const ORDER = ['rising', 'sun', 'moon'];

/**
 * Big Three tab: intro, planet arc, the selected placement, then `children` (the
 * host's CTAs). `onSelect(key, sign)` reports a tap so the host can track it.
 */
const BigThree = ({ bigThree, onSelect, children }) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState('sun');
    const current = bigThree[selected] || bigThree.sun;

    const select = (key) => {
        setSelected(key);
        onSelect?.(key, bigThree[key]?.sign || '');
    };

    return (
        <div id="bc-panel-bigThree" role="tabpanel" aria-labelledby="bc-tab-bigThree">
            <h3 className={styles.sectionTitle}>{t('birthChart.view.bigThree.title')}</h3>
            <p className={styles.intro}>{bigThree.intro}</p>

            <div className={styles.arc}>
                <svg className={styles.arcLine} viewBox="0 0 340 150" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M52 132C84 28 256 28 288 132" fill="none" />
                </svg>
                {ORDER.map((key) => {
                    const entry = bigThree[key];
                    const locked = !entry;
                    return (
                        <button
                            key={key}
                            type="button"
                            className={classNames(styles.bigThreeItem, styles[`bigThree_${key}`], {
                                [styles.bigThreeActive]: !locked && selected === key,
                                [styles.bigThreeLocked]: locked
                            })}
                            aria-pressed={locked ? undefined : selected === key}
                            aria-disabled={locked || undefined}
                            aria-label={
                                locked
                                    ? t('birthChart.view.bigThree.lockedLabel', { planet: t(`birthChart.view.bigThree.${key}`) })
                                    : undefined
                            }
                            onClick={locked ? undefined : () => select(key)}
                        >
                            {locked ? (
                                <LockedArt />
                            ) : key === 'rising' ? (
                                <RisingArt />
                            ) : (
                                <img className={styles.bigThreeImg} src={PLANET_IMAGES[key]} alt="" />
                            )}
                            <span className={styles.bigThreeLabel}>{entry?.label || t(`birthChart.view.bigThree.labels.${key}`)}</span>
                            <span className={styles.bigThreeSign}>{entry?.sign || t('birthChart.view.bigThree.lockedSign')}</span>
                        </button>
                    );
                })}
            </div>
            <p className={styles.hint}>
                <span className={styles.hintIcon} aria-hidden="true">
                    i
                </span>
                {t('birthChart.view.bigThree.hint')}
            </p>

            <section className={styles.placement} data-testid="bigthree-detail">
                <h3 className={styles.eyebrow}>{current.heading}</h3>
                <p className={styles.placementCopy}>{current.copy}</p>
                {current.traits?.length > 0 && (
                    <>
                        <h3 className={styles.eyebrow}>{t('birthChart.view.bigThree.traits')}</h3>
                        <ul className={styles.chips}>
                            {current.traits.map((trait) => (
                                <li key={trait} className={styles.chip}>
                                    {trait}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </section>

            {children}
        </div>
    );
};

export default BigThree;
